import { TextButton } from '@creditornot/cb-button';
import { Note as NoteIcon, Trashbin } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { KybSubmissionDocument } from 'apis/legitimizer/types';
import { Typography as T } from 'common/typography';

type DocumentViewerProps = {
  document: KybSubmissionDocument;
  label?: (fileName: string | null) => React.ReactNode;
  onRemove?: (documentId: number) => void;
};

const Root = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding-block-start: 12px;
  padding-block-end: 12px;
`;

const StyledIcon = styled(NoteIcon)`
  height: 32px;
  width: 32px;
  fill: ${cbSemanticCssVars.textTertiary};
  flex-shrink: 0;
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  color: ${cbSemanticCssVars.textPrimary};
`;

const Subtitle = styled(T.Small)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const formatISODate = (date: string) => {
  return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_SHORT);
};

export const DocumentViewer = ({ document, label, onRemove }: DocumentViewerProps) => {
  const { submittedAt, originalFilename } = document;

  return (
    <Root>
      <LeftContainer>
        <StyledIcon />

        <LabelContainer>
          <Title>
            <T.Body3>{label ? label(originalFilename) : originalFilename}</T.Body3>
          </Title>

          <Subtitle>
            <FormattedMessage
              id="compliance.kyb.file-upload.document-upload-info"
              values={{
                timestamp: formatISODate(submittedAt),
              }}
            />
          </Subtitle>
        </LabelContainer>
      </LeftContainer>

      {onRemove && (
        <TextButton size="small" icon={<Trashbin />} onClick={() => onRemove(document.id)}>
          <FormattedMessage id="compliance.kyb.file-upload.remove-document" />
        </TextButton>
      )}
    </Root>
  );
};
